import { useEffect, useState } from 'react';

declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp;
    };
  }
}

interface TelegramWebApp {
  ready: () => void;
  expand: () => void;
  close: () => void;
  enableClosingConfirmation: () => void;
  disableClosingConfirmation: () => void;
  sendData: (data: string) => void;
  initData: string;
  initDataUnsafe: {
    user?: {
      id: number;
      first_name: string;
      last_name?: string;
      username?: string;
      language_code?: string;
    };
    query_id?: string;
    start_param?: string;
  };
  MainButton: {
    text: string;
    color: string;
    textColor: string;
    isVisible: boolean;
    isActive: boolean;
    isProgressVisible: boolean;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    showProgress: (leaveActive?: boolean) => void;
    hideProgress: () => void;
    onClick: (callback: () => void) => void;
    offClick: (callback: () => void) => void;
    setText: (text: string) => void;
    setParams: (params: { text?: string; color?: string; text_color?: string; is_active?: boolean; is_visible?: boolean }) => void;
  };
  BackButton: {
    isVisible: boolean;
    show: () => void;
    hide: () => void;
    onClick: (callback: () => void) => void;
    offClick: (callback: () => void) => void;
  };
  themeParams: {
    bg_color?: string;
    text_color?: string;
    hint_color?: string;
    link_color?: string;
    button_color?: string;
    button_text_color?: string;
    secondary_bg_color?: string;
  };
  colorScheme: 'light' | 'dark';
  headerColor: string;
  backgroundColor: string;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
}

export function useTelegram() {
  const [tg, setTg] = useState<TelegramWebApp | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const telegram = window.Telegram?.WebApp;
    if (telegram) {
      telegram.ready();
      telegram.expand();
      setTg(telegram);
      setIsReady(true);
    } else {
      // For development without Telegram
      console.warn('Telegram WebApp not available, running in dev mode');
      setIsReady(true);
    }
  }, []);

  const user = tg?.initDataUnsafe?.user;
  const queryId = tg?.initDataUnsafe?.query_id;

  // Get carId from multiple sources
  // 1. start_param (from deep link t.me/bot?startapp=carId)
  // 2. URL query param ?carId=xxx (from webApp button)
  // 3. tgWebAppStartParam in hash (Telegram may put it there)
  const getCarId = (): string | undefined => {
    // Try start_param first
    if (tg?.initDataUnsafe?.start_param) {
      return tg.initDataUnsafe.start_param;
    }

    // Try URL query param
    const urlParams = new URLSearchParams(window.location.search);
    const carIdFromUrl = urlParams.get('carId');
    if (carIdFromUrl) {
      return carIdFromUrl;
    }

    // Try hash params (Telegram sometimes puts params in hash)
    const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
    const carIdFromHash = hashParams.get('carId') || hashParams.get('tgWebAppStartParam');
    if (carIdFromHash) {
      return carIdFromHash;
    }

    return undefined;
  };

  const startParam = getCarId();

  const sendData = (data: object) => {
    if (tg) {
      tg.sendData(JSON.stringify(data));
    } else {
      console.log('Would send data:', data);
    }
  };

  const close = () => {
    if (tg) {
      tg.close();
    }
  };

  return {
    tg,
    user,
    queryId,
    startParam,
    isReady,
    sendData,
    close,
  };
}
